import React from 'react';
import { arrayOf, func, shape } from 'prop-types';
import { DataLoadingCard, NoAvailableCard } from 'common';

// Components
import { Box, Button, Chip } from '@mui/material';
import {
  DataGridPremium,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-premium';

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarColumnsButton variant="dataTable" />
    <GridToolbarFilterButton variant="dataTable" />
    <GridToolbarDensitySelector variant="dataTable" />
    <GridToolbarExport variant="dataTable" />
  </GridToolbarContainer>
);

const UserTable = ({ affiliates, users, setSelectedUser }) => {
  const keyedAffiliates = affiliates.reduce((a, c) => {
    return { ...a, [`${c.companyCustomerIdName}-${c.dataCompanyId}`]: c.nameShort };
  }, {});

  const columns = [
    { field: 'userId', headerName: 'User ID', width: 150 },
    {
      field: 'userName',
      headerName: 'Username (Email)',
      width: 350,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
    },
    {
      field: 'affiliates',
      headerName: 'Affiliates',
      renderCell: params => (
        <Box sx={{ lineBreak: 'word-wrap' }}>
          {params.row.affiliates.map(a => {
            return (
              <Chip
                key={`${a.companyCustomerIdName}-${a.dataCompanyId}`}
                label={`${keyedAffiliates[`${a.companyCustomerIdName}-${a.dataCompanyId}`]} ${
                  a.companyCustomerIdName
                }-${a.dataCompanyId}`}
                sx={{
                  '& > .MuiChip-label': { px: 0 },
                  backgroundColor: 'surface.medium',
                  borderRadius: 0.5,
                  display: 'inline-block',
                  float: 'left',
                  fontSize: 12,
                  height: 20,
                  margin: 0.25,
                  px: 1,
                  py: 0.5,
                }}
              />
            );
          })}
        </Box>
      ),
      sortable: false,
      width: 400,
    },
    {
      cellClassName: 'actions',
      field: 'actions',
      headerName: 'User Permissions',
      renderCell: params => {
        return (
          <Button
            alt="Edit Permissions"
            data-testid="EditPermissionButton"
            onClick={() => {
              setSelectedUser(params.row.userId);
            }}
            variant="outlined"
          >
            Edit Permissions
          </Button>
        );
      },
      width: 200,
    },
  ];

  return (
    <Box>
      <DataGridPremium
        columns={columns}
        /* 
            Work around for 'Filter panel breaks layout on small display #5183'
            https://github.com/mui/mui-x/issues/5183
        */
        disableRowSelectionOnClick
        getRowId={row => row.userId}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 20,
            },
          },
        }}
        pageSizeOptions={[10, 20, 100, 250]}
        pagination
        rowHeight={70}
        rows={users || []}
        slotProps={{
          filterPanel: { sx: { maxWidth: '100vw' } },
        }}
        slots={{
          LoadingOverlay: () => (
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            <Box
              style={{
                height: '100%',
              }}
              sx={{ mt: { lg: 0, xs: 6 } }}
            >
              <DataLoadingCard type="data" />
            </Box>
          ),
          NoRowsOverlay: () => (
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            <Box
              sx={{
                mt: { lg: 0, xs: 6 },
              }}
            >
              <NoAvailableCard type="data" />
            </Box>
          ),
          Toolbar: CustomToolbar,
        }}
      />
    </Box>
  );
};

UserTable.propTypes = {
  affiliates: arrayOf(shape({})).isRequired,
  setSelectedUser: func.isRequired,
  users: arrayOf(shape({})),
};

UserTable.defaultProps = {
  users: [],
};

export default UserTable;
